import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import paths from "../../../utils/constants";
import { BreadCrumbSeparatorIcon } from "./Icons";
import api from "../../../api/api";
import { logMessage } from "../../../utils/helpers";
import { cn } from "../../../lib/utils";

const PURPLE = "#552F83";
const PAGE_BG = "#F1ECFF";

function isApiSuccess(res) {
  if (res == null) return false;
  const s = res.status;
  return s === 1 || s === "1" || Number(s) === 1;
}

function getStatusBgColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "redeemed") return "bg-[#552F83]";
  if (s === "available") return "bg-[#22C55E]";
  if (s === "expired") return "bg-[#EF4444]";
  return "bg-[#552F83]";
}

const InfoIcon = () => (
  <span
    className="inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 text-white text-xs font-bold"
    style={{ backgroundColor: PURPLE }}
  >
    i
  </span>
);

const RewardDetailPanel = ({ startLoading, stopLoading }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const userRewardId =
    location.state?.userRewardId || searchParams.get("userRewardId") || "";

  const [reward, setReward] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [redeeming, setRedeeming] = useState(false);

  const loadersRef = useRef({ startLoading, stopLoading });
  loadersRef.current = { startLoading, stopLoading };

  const load = useCallback(async () => {
    if (!userRewardId) {
      setLoading(false);
      setError(true);
      return;
    }

    const { startLoading: sl, stopLoading: el } = loadersRef.current;
    setLoading(true);
    setError(false);
    sl?.();
    try {
      const res = await api.getRewardDetails({ userRewardId });

      if (res && isApiSuccess(res) && res.data) {
        setReward(res.data);
      } else {
        logMessage(`getRewardDetails non-success: ${res?.message || ""}`, "warn");
        setReward(null);
        setError(true);
      }
    } catch (e) {
      logMessage(e);
      setReward(null);
      setError(true);
    } finally {
      setLoading(false);
      el?.();
    }
  }, [userRewardId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleRedeem = useCallback(async () => {
    if (!userRewardId || redeeming) return;

    const { startLoading: sl, stopLoading: el } = loadersRef.current;
    setRedeeming(true);
    sl?.();
    try {
      const res = await api.redeemedReward({ userRewardId });

      if (res && isApiSuccess(res)) {
        toast.success(res.message || "Reward redeemed successfully.");
        const detailRes = await api.getRewardDetails({ userRewardId });
        if (detailRes && isApiSuccess(detailRes) && detailRes.data) {
          setReward(detailRes.data);
        } else {
          setReward((prev) => (prev ? { ...prev, status: "redeemed" } : prev));
        }
      } else {
        toast.error(res?.message || "Could not redeem reward. Please try again.");
        logMessage(`redeemedReward non-success: ${res?.message || ""}`, "warn");
      }
    } catch (e) {
      logMessage(e);
      toast.error("Could not redeem reward. Please try again.");
    } finally {
      setRedeeming(false);
      el?.();
    }
  }, [userRewardId, redeeming]);

  return (
    <div className="relative min-h-full pb-10" style={{ backgroundColor: PAGE_BG }}>
      <div className="relative z-10 px-4 pt-4 pb-0">
        <p className="text-[12px] text-[#7C7790] flex items-center flex-wrap gap-0">
          <button
            type="button"
            className="cursor-pointer hover:underline text-[#323232] bg-transparent border-0 p-0 font-inherit"
            onClick={() => navigate(paths.YOUR_PROGRESS)}
          >
            Your progress
          </button>
          <BreadCrumbSeparatorIcon className="px-1.5 size-2.5 shrink-0 text-[#323232]" />
          <button
            type="button"
            className="cursor-pointer hover:underline text-[#323232] bg-transparent border-0 p-0 font-inherit"
            onClick={() => navigate(paths.REWARD_PANEL)}
          >
            My Rewards
          </button>
        </p>
      </div>

      <div className="relative z-20 px-4 pt-0!">
        <div className="mx-auto max-w-full">
          {loading ? (
            <p className="text-center text-sm text-[#7C7790] py-12">Loading reward details…</p>
          ) : error || !reward ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <p className="text-sm text-[#7C7790] mb-4 text-center">
                Could not load reward details. Please try again.
              </p>
              <button
                type="button"
                onClick={() => navigate(paths.REWARD_PANEL)}
                className="px-6 py-2.5 rounded-full text-white text-sm font-semibold hover:opacity-90 transition-opacity"
                style={{ backgroundColor: PURPLE }}
              >
                Back to My Rewards
              </button>
            </div>
          ) : (
            <div
              className="rounded-[20px] bg-transparent pt-[-10px] px-5 pb-5 md:px-6 md:pb-6 flex flex-col gap-5"
              // style={{ boxShadow: "0px 0px 4px 0px #0000001A" }}
            >
              {/* Hero banner */}
              <div className="relative pt-6 w-full">
                <div
                  className="absolute left-1/2 -translate-x-1/2 -top-0 z-10 w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: PURPLE }}
                >
                  {reward.main_icon ? (
                    <img
                      src={reward.main_icon}
                      alt={reward.name}
                      className="w-10 h-10 object-contain"
                    />
                  ) : (
                    <span className="text-2xl">🏆</span>
                  )}
                </div>

                <div
                  className="relative rounded-[16px] px-4 pt-12 pb-5 flex flex-col items-center gap-3"
                  style={{
                    background: "linear-gradient(90deg, #552F83 0%, #9B3FA8 50%, #C13584 100%)",
                  }}
                >
                  {reward.status && (
                    <span
                      className={cn(
                        "absolute right-3 top-3 text-xs font-semibold px-3 py-1 rounded-full capitalize text-white",
                        getStatusBgColor(reward.status)
                      )}
                    >
                      {reward.status}
                    </span>
                  )}

                  <span className="text-xl md:text-2xl font-bold text-white text-center leading-tight px-2">
                    {reward.name}
                  </span>

                  {reward.description && (
                    <span className="text-[10px] md:text-[11px] text-white/90 text-center leading-snug tracking-wide px-2 max-w-md">
                      TRIGGERED BY: {reward.description}
                    </span>
                  )}

                  {reward.expiry_at && (
                    <span className="text-xs text-white/80 text-center">
                      {(reward?.redeemed_at && reward?.status === "redeemed") ? "Redeemed At: " : reward?.status == "expired" ? "Expired At: " : "Valid until: "} {(reward?.redeemed_at && reward?.status === "redeemed") ? reward?.redeemed_at : reward.expiry_at}
                    </span> 
                  )}
                </div>
              </div>

              {/* Message quote */}
              {reward.message && (
                <p className="text-xs text-[#323232] text-center leading-relaxed px-2">
                  &ldquo;{reward.message}&rdquo;
                </p>
              )}

              {/* Benefits */}
              {reward.benefits?.length > 0 && (
                <div className="flex flex-col gap-3">
                  <h2 className="text-base font-bold text-[#323232]">
                    What You&apos;ve Received
                  </h2>
                  <div className="flex flex-col gap-2.5">
                    {reward.benefits.map((benefit, index) => (
                      <div
                        key={index}
                        className="rounded-[9px] bg-white px-4 my-auto py-4 text-sm text-[#323232] border border-[#F0ECF8]"
                        style={{ boxShadow: "0px 0px 4px 0px #0000000D" }}
                      >
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Good to know */}
              {reward.good_to_know && (
                <div
                  className="rounded-[14px] p-4 border border-[#552F83] z-1"
                  style={{
                    background: "linear-gradient(273.73deg, #FFFFFF -14.43%, #EEECFF 188.05%)",
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <InfoIcon />
                    <span className="text-sm font-bold" style={{ color: PURPLE }}>
                      Good to know
                    </span>
                  </div>
                  <p className="text-xs text-[#323232] leading-relaxed">
                    {reward.good_to_know}
                  </p>
                </div>
              )}

              {reward?.status === "available" && (
                <div className="w-full mx-auto flex items-center justify-center">
                  <button
                    type="button"
                    className="bg-[#552F83] px-12 py-1.5 flex items-center justify-center text-white font-semibold text-base rounded-[8px] border-0 hover:bg-[#552F83]/80 disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={handleRedeem}
                    disabled={redeeming}
                  >
                    {redeeming ? "Redeeming…" : "Redeem"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardDetailPanel;
